import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Send, Bot, User, Moon, Sparkles, Eye, Shuffle, Heart, Star, Clock, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QUESTION_CATEGORIES = [
  {
    id: 'basics',
    name: 'Les bases',
    icon: Sparkles,
    color: 'bg-purple-100 border-purple-300 text-purple-700',
    questions: [
      "Qu'est-ce que le Tarot de Marseille ?",
      "Combien y a-t-il d'Arcanes Majeurs ?",
      "Le Tarot peut-il prédire l'avenir ?",
    ]
  },
  {
    id: 'cards',
    name: 'Les Cartes',
    icon: Moon,
    color: 'bg-indigo-100 border-indigo-300 text-indigo-700',
    questions: [
      "Que signifie la carte du Mat ?",
      "La Mort est-elle une carte négative ?",
      "Quelle est la signification de La Lune ?",
    ]
  },
  {
    id: 'spreads',
    name: 'Les Tirages',
    icon: Shuffle,
    color: 'bg-violet-100 border-violet-300 text-violet-700',
    questions: [
      "Comment faire un tirage en croix celtique ?",
      "Quel tirage pour une question simple ?",
      "Comment interpréter les positions des cartes ?",
    ]
  },
  {
    id: 'reversed',
    name: 'Cartes renversées',
    icon: Eye,
    color: 'bg-slate-100 border-slate-300 text-slate-700',
    questions: [
      "Que signifie une carte renversée ?",
      "Faut-il utiliser les cartes renversées ?",
      "Comment interpréter Le Soleil renversé ?",
    ]
  },
  {
    id: 'elements',
    name: 'Symbolisme',
    icon: Star,
    color: 'bg-amber-100 border-amber-300 text-amber-700',
    questions: [
      "Quels sont les 4 éléments du Tarot ?",
      "Que symbolise le nombre 7 dans le Tarot ?",
      "Quelle est la symbolique des couleurs ?",
    ]
  },
  {
    id: 'practice',
    name: 'Pratique',
    icon: Heart,
    color: 'bg-pink-100 border-pink-300 text-pink-700',
    questions: [
      "Comment formuler une bonne question ?",
      "À quelle fréquence peut-on tirer les cartes ?",
      "Comment développer son intuition avec le Tarot ?",
    ]
  },
];

const MysteriaeChatPage = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState('basics');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const welcomeMessage = `Bienvenue ! Je suis Arcana, experte en Tarot de Marseille. 🌙

Cet espace est dédié à l'apprentissage et à la compréhension du Tarot :
• Les significations des 22 Arcanes Majeurs
• Les techniques de tirage et d'interprétation
• Le symbolisme et l'histoire du Tarot
• Les conseils pour développer votre pratique

📖 Posez vos questions pour explorer les mystères des Arcanes !

⚠️ Note : Le Tarot est un outil d'introspection, pas de prédiction. Je ne fais pas de tirages personnalisés ici - utilisez la fonction "Faire un tirage" pour cela.`;
    
    setMessages([
      {
        role: 'assistant',
        content: welcomeMessage,
        timestamp: new Date()
      },
    ]);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (messageText = inputMessage) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage = {
      role: 'user',
      content: messageText,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/mysteriae/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: messageText
        })
      });

      const data = await response.json();

      if (data.success) {
        const assistantMessage = {
          role: 'assistant',
          content: data.answer,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, assistantMessage]);
      } else {
        throw new Error(data.detail || 'Erreur');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erreur lors de l\'envoi du message');
      
      const fallbackMessage = {
        role: 'assistant',
        content: "Je suis désolée, je rencontre un problème technique. Pour explorer les cartes en détail, je vous invite à consulter la section 'Découvrir' ou à faire un tirage personnalisé !",
        timestamp: new Date()
      };
      setMessages((prev) => [...prev, fallbackMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestedQuestion = (question) => {
    sendMessage(question);
  };

  const currentCategory = QUESTION_CATEGORIES.find(c => c.id === activeCategory);

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 via-white to-purple-50 flex flex-col">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-purple-200 p-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Button
            data-testid="back-button"
            variant="ghost"
            onClick={() => navigate('/mysteriae')}
            className="rounded-full text-gray-600 hover:bg-purple-50"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-3 flex-1">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
              <Bot className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Arcana - Expert Tarot</h1>
              <p className="text-xs text-purple-600">Posez vos questions sur les Arcanes</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/mysteriae/draw')}
            className="hidden sm:flex border-purple-200 text-purple-700 hover:bg-purple-50 rounded-xl"
          >
            <Shuffle className="h-4 w-4 mr-2" />
            Tirage
          </Button>
        </div>
      </div>

      {/* Main Content - Messages + Sidebar */}
      <div className="flex-1 flex overflow-hidden">
        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="max-w-3xl mx-auto space-y-4">
            <AnimatePresence>
              {messages.map((message, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      message.role === 'user'
                        ? 'bg-purple-500'
                        : 'bg-gradient-to-br from-purple-500 to-indigo-600'
                    }`}
                  >
                    {message.role === 'user' ? (
                      <User className="h-4 w-4 text-white" />
                    ) : (
                      <Bot className="h-4 w-4 text-white" />
                    )}
                  </div>
                  <div
                    className={`flex-1 max-w-[85%] ${
                      message.role === 'user' ? 'text-right' : ''
                    }`}
                  >
                    <div
                      className={`inline-block p-4 rounded-2xl ${
                        message.role === 'user'
                          ? 'bg-purple-500 text-white'
                          : 'bg-white border border-purple-200 text-gray-700 shadow-sm'
                      }`}
                    >
                      <div className="text-sm leading-relaxed prose prose-sm max-w-none">
                        <ReactMarkdown>{message.content}</ReactMarkdown>
                      </div>
                    </div>
                    <p className={`text-xs mt-1 px-2 ${message.role === 'user' ? 'text-gray-400' : 'text-gray-400'}`}>
                      {message.timestamp.toLocaleTimeString('fr-FR', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {isLoading && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex gap-3"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                  <Bot className="h-4 w-4 text-white" />
                </div>
                <div className="bg-white border border-purple-200 p-4 rounded-2xl shadow-sm">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" />
                      <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}} />
                      <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}} />
                    </div>
                    <span className="text-xs text-purple-600 ml-2">Arcana réfléchit...</span>
                  </div>
                </div>
              </motion.div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Sidebar - Categories & Questions */}
        <div className="hidden lg:block w-80 border-l border-purple-200 bg-white/50 overflow-y-auto">
          {/* Category Tabs */}
          <div className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-purple-100 p-3">
            <h3 className="text-sm font-semibold mb-3 text-gray-800">Catégories</h3>
            <div className="flex flex-wrap gap-1.5">
              {QUESTION_CATEGORIES.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`px-2.5 py-1.5 text-xs rounded-full border transition-all ${
                    activeCategory === category.id
                      ? category.color + ' font-semibold'
                      : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {/* Questions for Selected Category */}
          <div className="p-4">
            <div className="flex items-center gap-2 mb-3">
              {currentCategory && <currentCategory.icon className="h-4 w-4 text-purple-600" />}
              <span className="text-xs font-semibold text-gray-700">{currentCategory?.name}</span>
            </div>
            <div className="space-y-2">
              {currentCategory?.questions.map((question, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestedQuestion(question)}
                  disabled={isLoading}
                  className="w-full px-3 py-2.5 text-xs rounded-xl border transition-colors text-left leading-relaxed disabled:opacity-50 disabled:cursor-not-allowed bg-white border-purple-200 text-gray-600 hover:bg-purple-50 hover:border-purple-300"
                >
                  {question}
                </button>
              ))}
            </div>
            
            {/* CTA */}
            <div className="mt-6 p-4 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl border border-purple-200">
              <p className="text-xs text-purple-700 mb-3 font-medium">
                Envie de tirer les cartes ?
              </p>
              <Button
                onClick={() => navigate('/mysteriae/draw')}
                size="sm"
                className="w-full rounded-full bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-400 hover:to-indigo-500 text-white text-xs"
              >
                <Sparkles className="mr-2 h-3 w-3" />
                Faire un tirage
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Categories & Questions */}
      <div className="lg:hidden border-t border-purple-200 p-3 bg-white/80 backdrop-blur-md">
        <div className="max-w-4xl mx-auto">
          {/* Category Pills */}
          <div className="flex gap-2 overflow-x-auto pb-2 mb-2 scrollbar-hide">
            {QUESTION_CATEGORIES.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`flex-shrink-0 px-3 py-1.5 text-xs rounded-full border transition-all ${
                  activeCategory === category.id
                    ? category.color + ' font-semibold'
                    : 'bg-gray-50 border-gray-200 text-gray-500'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
          {/* Questions */}
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {currentCategory?.questions.map((question, index) => (
              <button
                key={index}
                onClick={() => handleSuggestedQuestion(question)}
                disabled={isLoading}
                className="flex-shrink-0 px-3 py-2 text-xs bg-white hover:bg-purple-50 disabled:opacity-50 rounded-full border border-purple-200 transition-colors whitespace-nowrap text-gray-600"
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Input */}
      <div className="border-t-2 border-purple-200 p-4 bg-white/90 backdrop-blur-md shadow-lg">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-medium text-purple-600">🌙 Question libre</span>
          </div>
          <div className="flex gap-3">
            <input
              data-testid="chat-input"
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              placeholder="Posez votre question sur le Tarot..."
              className="flex-1 px-4 py-3 rounded-full border-2 border-purple-200 bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-400"
              disabled={isLoading}
            />
            <Button
              data-testid="send-button"
              onClick={() => sendMessage()}
              disabled={!inputMessage.trim() || isLoading}
              className="rounded-full w-12 h-12 p-0 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-400 hover:to-indigo-500"
            >
              <Send className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MysteriaeChatPage;
